import { useEffect, useState } from "react";

import { dayMonthMaybeYear } from "@/lib/dates";
import {
  statsBacktest,
  statsHabit,
  statsHistory,
  type StatsBacktest,
  type StatsHabit,
  type StatsHistory,
} from "@/lib/stats";

import { SkeletonChart, SkeletonRows, StatSkeleton } from "./Skeleton";
import { Panel, StatList, StatValue, Td, Th } from "./Panel";
import { Bars, type Bar } from "./chart/Bars";
import { HeatmapLegend, YEAR_ROW_HEIGHT, YearGrid } from "./chart/Heatmap";
import { useChartHover } from "./chart/HoverCard";

/**
 * Every panel's heading, in one place — because each of them is written **twice**: once by the
 * panel and once by that panel's skeleton (`ReviewsSkeleton` and its siblings), which `History`
 * draws in the same place while the request that fills it is in flight.
 *
 * Two copies of a title is two chances for the loading state and the loaded state to say different
 * things about the same card, and the reader sees them a tenth of a second apart. It was already
 * true before this was Japanese — the skeleton's load panel said `Load · 60 days back, 60 forward`
 * where the real one said `Load — 60 days back, 60 days forward` — and translating doubled the
 * surface for it. So the heading is data and both readers take it from here.
 *
 * The `check` ids travel with the titles deliberately: they are the `checks/` hooks and they stay
 * English, so keeping the pair together is what stops a rename touching one and not the other.
 */
const PANELS = {
  reviewsPerDay: { title: "1日の復習数", check: "reviews-per-day" },
  streak: { title: "連続日数", check: "current-streak" },
  figures: { title: "履歴", check: "history" },
  load: { check: "load", note: "1日あたりの復習数" },
  comparison: {
    title: "予測と同じ長さの過去との比較",
    check: "forecast-vs-the-equivalent-window-behind",
  },
  backlog: { title: "期限切れ未復習", check: "backlog" },
  limits: { title: "上限適用前後の予測", check: "capped-vs-raw-forecast" },
  accuracy: { title: "予測精度", check: "forecast-accuracy", note: "予測と予定を実測と比較" },
} as const;

/**
 * `負荷 — 前後60日`, the canvas's own title, built from the axis's real length.
 *
 * A function rather than a constant because the panel knows the window (`(bars.length - 1) / 2`)
 * and the skeleton does not — it draws before any bars exist. `LOAD_HALF_WINDOW` is the server's
 * default and is only ever the skeleton's guess; the moment data lands the panel restates it from
 * what actually arrived.
 */
const loadTitle = (halfWindow: number) => `負荷 — 前後${halfWindow}日`;
const LOAD_HALF_WINDOW = 60;

/** The two units a `StatValue` and its `StatSkeleton` both have to spell. */
const UNITS = {
  streak: "日",
  backlog: "枚が未復習",
} as const;

/**
 * History — `8a`. What I've done, and what's coming.
 *
 * The tab holds spec sections A *and* C, because A's reviews-per-day series **is** the left half of
 * C's unified axis. Splitting them would put the same data on two screens at two scales, which is
 * exactly the failure the unified axis exists to fix.
 */
export function History({ deck }: { deck: string }) {
  const [habit, setHabit] = useState<StatsHabit | null>(null);
  const [data, setData] = useState<StatsHistory | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * **Two requests, in order, and the left column fills from the first** (日本-718).
   *
   * `/history` runs `graphs()` and two FSRS simulations — ~5.7 s on production cold, which is every
   * visit after a review sitting — and the whole tab used to wait on it. The left column (the grid,
   * the streak, its figures) needs only the revlog scan, so `/habit` answers it first and the
   * forecast's panels keep their skeletons until `/history` lands.
   *
   * `/history` is issued once `/habit` has answered, never beside it: both run on the batch handle's
   * one worker, and racing them could queue the cheap read behind the expensive one. It does not
   * pay for the scan twice — `habit_days` is memoised on the collection's contents.
   */
  useEffect(() => {
    let live = true;
    setHabit(null);
    setData(null);
    setError(null);
    const full = () =>
      statsHistory(deck)
        .then((found) => live && setData(found))
        .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    void statsHabit(deck)
      .then((found) => live && setHabit(found))
      // A failed habit read is not the tab's error: `/history` carries the same panels, and it is
      // the one whose failure leaves something undrawable.
      .catch(() => {})
      .then(() => live && full());
    return () => {
      live = false;
    };
  }, [deck]);

  // Every panel is drawn in its designed place from the first frame and fills as its own request
  // lands — the left column from `/habit`, the right from `/history`. See `Skeleton.tsx`.
  const past: StatsHabit | null = data ?? habit;
  const failed = error ? <p className="p-[26px] text-secondary text-accent-text">{error}</p> : null;
  // A failed `/history` with nothing drawn yet takes the tab, as it always did. Once the left
  // column has drawn from `/habit`, it takes only the right column: replacing a grid the reader is
  // already looking at with an error about the forecast would throw away the half that worked.
  if (failed && !past) return failed;

  return (
    // **Three layouts, and the widest one is the frame's** (`1920 × 1080 — fits without
    // scrolling`, 8 Aug 2026). Below 1024 everything stacks. From 1024 the frames' 1147 layout: the
    // heatmap beside a narrow column holding the streak and its figures, the load axis full width,
    // the comparison beside the backlog. From `2xl` the handoff's rule — *the sidebar cards unpair
    // from their rows into a second column, and the heatmap and load axis keep their 1147 geometry
    // rather than stretching* — so History becomes two columns of widgets: what has happened on the
    // left, what is coming on the right.
    //
    // 1536 is Tailwind's `2xl` and the handoff says "above ~1500px", which is the same line. It is
    // the right one for this machine too: a 3440 ultrawide at ⅓ is ~1147 (the narrow layout, by
    // construction) and at ⅔ is ~2293 (the wide one).
    <div className="flex flex-col gap-[14px] 2xl:flex-row 2xl:items-start">
      {/* Left: what has happened. */}
      <div className="flex min-w-0 flex-col gap-[14px] 2xl:flex-1">
        <div className="flex flex-col gap-[14px] lg:flex-row 2xl:flex-col">
          <div className="min-w-0 flex-1">
            {past ? <ReviewsPanel data={past} /> : <ReviewsSkeleton />}
          </div>
          {/* The pair that unpairs: a 250px sidebar at 1147, two equal cards under the grid at
              1920. Wrappers rather than props on the panels, so `Panel` stays a component about a
              card and knows nothing about where it is being placed.

              **`2xl:flex-1` on those wrappers, never a bare `flex-1`.** At 1147 this column is a
              flex *item* of the row beside the heatmap, so it is as tall as the heatmap — and
              growing children would split ~650px between two short cards, leaving a canyon between
              them that reads as a missing panel. They only want to share space when this is
              genuinely a row. */}
          <div className="flex shrink-0 flex-col gap-[14px] lg:w-[250px] 2xl:w-auto 2xl:flex-row">
            <div className="min-w-0 2xl:flex-1">
              {past ? <StreakPanel data={past} /> : <StreakSkeleton />}
            </div>
            <div className="min-w-0 2xl:flex-1">
              {past ? <HistoryFiguresPanel data={past} /> : <FiguresSkeleton />}
            </div>
          </div>
        </div>
      </div>

      {/* Right: what is coming. */}
      <div className="flex min-w-0 flex-col gap-[14px] 2xl:flex-1">
        {failed ?? (
          <>
            {data ? <LoadPanel data={data} /> : <LoadSkeleton />}
            <div className="flex flex-col gap-[14px] lg:flex-row 2xl:flex-col">
              <div className="min-w-0 flex-1">
                {data ? <ComparisonPanel data={data} /> : <ComparisonSkeleton />}
              </div>
              <div className="flex shrink-0 flex-col gap-[14px] lg:w-[326px] 2xl:w-auto 2xl:flex-row">
                <div className="min-w-0 2xl:flex-1">
                  {data ? <BacklogPanel data={data} /> : <BacklogSkeleton />}
                </div>
                <div className="min-w-0 2xl:flex-1">
                  {data ? <LimitsPanel data={data} /> : <LimitsSkeleton />}
                </div>
              </div>
            </div>
          </>
        )}
        {/* Its own request, on its own route: it needs the full revlog scan and nobody is waiting
            on it, so it must not make the tab's first paint wait — `/trend`'s reasoning. */}
        <AccuracyPanel deck={deck} />
      </div>
    </div>
  );
}

// **The habit scope is no longer named on screen** (Alex, 8 Aug 2026: *"I know it, and don't need
// it shown"*). These three panels are still drawn from a wider population than the rest of the tab —
// the chosen deck's parent, suspended cards included, because they answer *did you study* rather
// than *how are these cards doing* — and `habitDeck` still comes down the wire so a future reader
// can see which. It is simply not worth a line of chrome on every one of them for an audience of
// one who already knows.

function ReviewsPanel({ data }: { data: StatsHabit }) {
  const { heatmap } = data;
  // One hover state per panel, not per grid: two cards on screen at once is the failure, and a
  // pointer can only be over one cell.
  const { onEnter, onLeave, card } = useChartHover();
  return (
    <Panel
      {...PANELS.reviewsPerDay}
      note={`${heatmap.totalDays.toLocaleString()}日 · 学習${heatmap.studiedDays.toLocaleString()}日（${(heatmap.shareStudied * 100).toFixed(1)}%）· 未実施${heatmap.missedDays.toLocaleString()}日${heatmap.inProgress ? " · 今日は進行中" : ""}`}
      right={<HeatmapLegend thresholds={heatmap.thresholds} inProgress={heatmap.inProgress} />}
      gap={11}
    >
      {/* Year number LEFT of the grids, each year's figures RIGHT — the frames' arrangement, and
          **three columns rather than one row per year**.

          The grid is the widest thing on the screen and the app forbids the PAGE scrolling
          horizontally at any width, so the grids scroll inside their own box: seven years of 9px
          cells is ~500px per year block, which fits at 1147 and does not at 390. Built as a row per
          year, the figures sat *inside* that scroller and were clipped off the panel's right edge —
          `perfect yea`, on every year, at the width this actually renders at. Only the middle
          column scrolls; the other two are pinned, and `YEAR_ROW_HEIGHT` is what keeps all three
          aligned. */}
      <div className="flex items-start gap-[10px]">
        <div className="flex shrink-0 flex-col gap-[9px]">
          {heatmap.years.map((year) => (
            <span
              key={year.year}
              className="flex w-[34px] justify-end pt-[1px] font-mono text-[14px]/[14px] text-ink-3"
              style={{ height: YEAR_ROW_HEIGHT }}
            >
              {year.year}
            </span>
          ))}
        </div>

        {/* **`p-[4px] -m-[4px]`, and the 4 is the hover ring's own reach.** The ring is drawn
            OUTSIDE a cell's box (`0 0 0 3.5px`), and this is a scroll container — `overflow-x: auto`
            computes `overflow-y` to `auto` as well, so anything outside the padding box on any edge
            is clipped rather than merely overhanging. That took the ring off the top row, the bottom
            row and the leftmost column: three edges, one cause, and it looked like three bugs.
            Padding grows the padding box to contain the ring; the equal negative margin leaves the
            margin box — which is what the row lays out — exactly where it was, so no sibling moves.
            The same pair `Read/TabStrip.tsx` uses, for the same reason. */}
        <div className="-m-[4px] min-w-0 flex-1 overflow-x-auto p-[4px]">
          {/* `items-start` is load-bearing, not tidiness. A grid child of a flex column stretches
              to the column's width by default, and the column is as wide as the widest full year —
              so the current, part-finished year had its ~32 auto-width columns spread across a
              53-column box and rendered as evenly spaced stripes. It looked like a rendering bug in
              the heatmap and was one line of alignment. */}
          <div className="flex min-w-max flex-col items-start gap-[9px]">
            {heatmap.years.map((year) => (
              <YearGrid key={year.year} year={year} onEnter={onEnter} onLeave={onLeave} />
            ))}
          </div>
        </div>

        <div className="flex shrink-0 flex-col gap-[9px]">
          {heatmap.years.map((year) => (
            <div
              key={year.year}
              className="flex w-[104px] flex-col items-end gap-[2px] pt-[1px]"
              style={{ height: YEAR_ROW_HEIGHT }}
            >
              <span className="font-mono text-[14px]/[14px] text-ink-3">
                {year.average.toFixed(0)}回/日
              </span>
              {/* `未実施`, never "blank" — the copy is specified, and it is the honest word for
                  what the ringed cells mean. It is the same word the legend beside it uses for
                  the same swatch, which is what lets the two be read together. */}
              <span
                className="whitespace-nowrap font-mono text-[14px]/[14px]"
                style={{ color: year.perfect ? "var(--ink-3)" : "var(--accent-text)" }}
              >
                {year.perfect ? "未実施なし" : `未実施${year.missed}日`}
              </span>
            </div>
          ))}
        </div>
      </div>
      {card}
    </Panel>
  );
}

function StreakPanel({ data }: { data: StatsHabit }) {
  const { streak } = data;
  return (
    <Panel {...PANELS.streak} gap={7}>
      <StatValue
        value={streak.current.toLocaleString()}
        unit={UNITS.streak}
        size={46}
        note={
          (streak.started
            ? `${dayMonthMaybeYear(new Date(`${streak.started}T00:00:00`))} から`
            : "連続なし") +
          (streak.current > 0 && streak.current >= streak.longest ? " · 記録更新中" : "")
        }
      />
    </Panel>
  );
}

/**
 * Every figure here is computed from the SAME day series the grid renders, which is what makes it
 * impossible for the streak to imply zero missed days while the grid shows some — STATS.md names
 * that as this panel's failure mode.
 */
function HistoryFiguresPanel({ data }: { data: StatsHabit }) {
  const { streak, heatmap } = data;
  return (
    <Panel {...PANELS.figures} gap={7}>
      <StatList
        rows={[
          ["これまでの最長", `${streak.longest.toLocaleString()}日`],
          ["学習日数", streak.daysStudied.toLocaleString()],
          [
            "未実施日数",
            `${streak.daysMissed.toLocaleString()} · ${(heatmap.shareStudied * 100).toFixed(1)}% 学習`,
          ],
          ["通算の復習回数", streak.totalReviews.toLocaleString()],
        ]}
      />
    </Panel>
  );
}

function LoadPanel({ data }: { data: StatsHistory }) {
  const { load } = data;
  const { onEnter, onLeave, card } = useChartHover();
  // **`newPerDay`, not `bars.some(b => b.atLimit)`.** It is the same fact the server used to decide
  // whether to run the simulation at all, so the two cannot drift — and an all-zero series and an
  // uncomputed one are indistinguishable from here, where only the first deserves a key.
  const keepsMining = data.limits.newPerDay > 0;
  const bars: Bar[] = load.bars.map((bar) => {
    const when = dayMonthMaybeYear(new Date(`${bar.day}T00:00:00`));
    const today = bar.offset === 0;
    return {
      value: bar.count,
      color: today ? "var(--load-today)" : bar.future ? "var(--load-future)" : "var(--load-past)",
      // The modelled forecast, on the same scale and behind the scheduled bar — 日本-340. Only on
      // the future half: today already has an honest answer and the past has a measured one.
      ghost:
        bar.future && bar.modelled > 0
          ? { value: bar.modelled, color: "var(--load-modelled)" }
          : undefined,
      // The keep-mining forecast — 日本-346, a rule above the other two marks. A rule rather than a
      // second outline because 60 future bars share a ~9px column: fill, outline and rule stay
      // separable there where three boxes do not.
      crest:
        bar.future && bar.atLimit > 0
          ? { value: bar.atLimit, color: "var(--load-at-limit)" }
          : undefined,
      hover: {
        title: today ? `${when} · 今日` : when,
        // **Today says both halves, and that is the whole reason the bar is a sum** (Alex, 8 Aug
        // 2026). The one figure Anki keeps for today is what is *left*, so a day with 285 done and
        // 4 remaining used to draw a bar of 4 and a card mentioning only the 4 — the quietest day
        // of the year on the day the most work happened. Every other bar has one honest number, so
        // it says one.
        rows: today
          ? [
              { label: "復習数", value: bar.count.toLocaleString() },
              { label: "実施済", value: bar.reviewed.toLocaleString() },
              { label: "未消化", value: bar.due.toLocaleString() },
            ]
          : [
              // The verb is the point: the same height means two different things either side of
              // today, and a card reading only a number would not say which.
              { label: bar.future ? "予定" : "実測", value: bar.count.toLocaleString() },
              // **All three, labelled, on every future day.** The gaps between them ARE the
              // finding: 予定 cannot contain a lapse, a new card or the next review of anything due
              // before the window, and 予測 additionally assumes you never mine another word.
              ...(bar.future
                ? [{ label: "予測", value: bar.modelled.toLocaleString() }]
                : []),
              ...(bar.future && keepsMining
                ? [{ label: "導入継続", value: bar.atLimit.toLocaleString() }]
                : []),
              {
                // The counter goes on the VALUE here, not into the label. English could put the
                // direction in the label and leave a bare integer beside it (`Days ahead · 12`);
                // Japanese cannot, because `後` / `前` is a suffix on the number. So the label is
                // the neutral `今日から` and the value carries both the figure and its direction.
                label: "今日から",
                value: `${Math.abs(bar.offset)}日${bar.future ? "後" : "前"}`,
              },
            ],
      },
    };
  });

  const first = load.bars[0];
  const last = load.bars[load.bars.length - 1];
  // **The scale has to contain the limit**, or the dashed rule clamps to the very top of the panel
  // and stops meaning anything — which is what it did on a deck whose forecast never approaches its
  // cap. The frames draw `400 cap` well above the bars, and that only works if 400 is on the axis.
  const scaleMax = Math.max(load.scaleMax, load.reviewLimit ?? 0);
  const limitShare = load.reviewLimit ? load.reviewLimit / scaleMax : null;

  return (
    <Panel
      {...PANELS.load}
      title={loadTitle((load.bars.length - 1) / 2)}
      right={
        <span className="flex items-center gap-[14px]">
          <Key color="var(--load-past)" label="実測" />
          <Key color="var(--load-future)" label="予定" />
          {/* Outlined in the legend as it is on the chart, so the key reads as the mark it names
              rather than as a third solid series. */}
          <span className="flex items-center gap-[5px] text-[14px]/[1.4] text-ink-2">
            <span
              aria-hidden
              className="h-[11px] w-[11px] rounded-[var(--r-2)] border"
              style={{ borderColor: "var(--load-modelled)" }}
            />
            予測
          </span>
          {keepsMining && (
            /* A RULE in the key, as it is on the chart — the legend has to name the mark, not just
               the colour, or a reader looking for a third box never finds it. */
            <span
              className="flex items-center gap-[5px] text-[14px]/[1.4] text-ink-2"
              /* The RATE, on demand rather than in a caption. "Continued introduction" does not on
                 its own say at what pace, and the deck's own limit is the whole assumption — but a
                 paragraph under the chart saying so is the apology-under-a-number this surface has
                 already deleted twice. */
              title={`1日${data.limits.newPerDay.toLocaleString()}枚で新規を入れ続けた場合`}
            >
              <span
                aria-hidden
                className="h-[2px] w-[12px]"
                style={{ background: "var(--load-at-limit)" }}
              />
              導入継続
            </span>
          )}
          {load.reviewLimit !== null && (
            <span className="flex items-center gap-[5px] text-[14px]/[1.4] text-ink-2">
              <span
                aria-hidden
                className="h-0 w-[12px] border-t border-dashed"
                style={{ borderColor: "var(--load-limit)" }}
              />
              1日{load.reviewLimit.toLocaleString()}回の上限
            </span>
          )}
        </span>
      }
      gap={8}
    >
      <div className="relative pt-[8px]">
        {/* ONE scale for both halves — a single `scaleMax` passed to a single `Bars`. This is the
            largest improvement over Anki in the whole document: its two-graph split on two scales
            is precisely why "is it getting heavier" cannot be answered there. */}
        <Bars bars={bars} scaleMax={scaleMax} height={120} onEnter={onEnter} onLeave={onLeave} />
        {limitShare !== null && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 h-0 border-t border-dashed"
            style={{ bottom: `${limitShare * 100}%`, borderColor: "var(--load-limit)" }}
          >
            {/* On paper, with a pixel of padding: the cap is often BELOW the peak — that is the
                whole reason to draw it — so a bare label lands among the bars and stops being
                readable exactly on the decks where it matters. */}
            <span
              className="absolute -top-[13px] left-0 rounded-[var(--r-2)] bg-panel px-[3px] font-mono text-[14px]/[15.4px]"
              style={{ color: "var(--load-limit)" }}
            >
              上限{load.reviewLimit?.toLocaleString()}
            </span>
          </span>
        )}
      </div>

      <div className="flex justify-between text-[14px]/[1.4] text-ink-4">
        <span>{first && dayMonthMaybeYear(new Date(`${first.day}T00:00:00`))}</span>
        <span className="font-semibold text-ink">
          今日 · {dayMonthMaybeYear(new Date(`${data.today}T00:00:00`))}
        </span>
        <span>{last && dayMonthMaybeYear(new Date(`${last.day}T00:00:00`))}</span>
      </div>
      {/* Under the chart it describes, not under the heatmap. Its sentence opens `Projected
          load: …`, so anywhere but here it reads as a caveat about whatever panel it landed in. */}
      <ForecastNote data={data} />
      {card}
    </Panel>
  );
}

function ComparisonPanel({ data }: { data: StatsHistory }) {
  const thirty = data.comparison.find((c) => c.windowDays === 30);
  const keepsMining = data.limits.newPerDay > 0;
  return (
    <Panel {...PANELS.comparison}>
      {/* The sentence the panel exists to produce — and it is drawn from the MODELLED figure, not
          the scheduled one (日本-340). `delta` compares a floor with a measurement: on a collection
          being added to daily it reported the coming month as far lighter than the last, which is
          the complaint that opened the ticket.

          The emphasised span holds the percentage AND its verb, which is what the English form
          did too — but here it has to, because 重く / 軽く is what turns the figure into a claim
          and a bare `12%` in bold would be an emphasis on nothing. */}
      {thirty && thirty.modelledDelta !== null && (
        <p className="text-[15px]/[1.5] text-ink-2">
          今後30日の予測は直近30日より{" "}
          <strong
            className="font-semibold"
            style={{ color: thirty.modelledDelta > 0 ? "var(--accent-text)" : "var(--ink-3)" }}
          >
            {Math.abs(thirty.modelledDelta * 100).toFixed(0)}%
            {thirty.modelledDelta > 0 ? "重く" : "軽く"}
          </strong>{" "}
          なります。
          {/* The keep-mining figure is a SECOND CLAUSE rather than a second Δ column — 日本-346.
              The table already carries one Δ, and 導入継続 平均 / 最大 has just taken it to six
              columns; a second Δ would make seven, which does not fit at 1147. It reads as a
              what-if here, which is what it is. */}
          {keepsMining && thirty.atLimitDelta !== null && (
            <>
              {/* No space after 。 — Japanese does not set one, and JSX's `{" "}` put a visible gap
                  between the two clauses. The second sentence simply follows the first. */}
              導入を続けた場合は{" "}
              <strong
                className="font-semibold"
                style={{
                  color: thirty.atLimitDelta > 0 ? "var(--accent-hi)" : "var(--ink-3)",
                }}
              >
                {Math.abs(thirty.atLimitDelta * 100).toFixed(0)}%
                {thirty.atLimitDelta > 0 ? "重く" : "軽く"}
              </strong>
              。
            </>
          )}
        </p>
      )}
      <table className="w-full">
        <thead>
          <tr>
            <Th align="left">期間</Th>
            {/* **Both, side by side** (Alex, 9 Aug 2026). `Scheduled` is what Anki has actually
                booked into those days — a floor, and structurally unable to contain a lapse, a new
                card, or the next review of anything due before the window. `Projected` is the FSRS
                simulation. Showing only one would either keep the number that reads wrong or hide
                the measured figure the model should be checked against. */}
            <Th>予定 平均 / 最大</Th>
            <Th>予測 平均 / 最大</Th>
            {/* Between 予測 and 過去, so the three forecasts sit in ascending order and the
                measured past stays adjacent to the Δ that is computed against it. */}
            {keepsMining && <Th>導入継続 平均 / 最大</Th>}
            <Th>過去 平均 / 最大</Th>
            <Th width={48}>Δ</Th>
          </tr>
        </thead>
        <tbody>
          {data.comparison.map((row) => (
            <tr key={row.windowDays} className="border-t border-line-soft">
              <Td align="left" mono={false}>{row.windowDays}日</Td>
              {/* The peak's date, because a figure that repeats down all three windows reads as a
                  bug until you can see it is one day inside all three of them. */}
              <Td tone="muted" title={`最大は${peakDay(row.aheadPeakDay)}`}>
                {row.aheadAverage.toFixed(0)} / {row.aheadPeak.toLocaleString()}
              </Td>
              {/* The peak's date is a `title` here rather than a printed figure: one Monte Carlo
                  trajectory puts a peak's DATE at a standard deviation of 37 days, where its
                  aggregates hold to about 1%. The averages can be read; the date cannot. */}
              <Td title={`予測の最大は${peakDay(row.modelledPeakDay)}前後`}>
                {row.modelledAverage.toFixed(0)} / {row.modelledPeak.toLocaleString()}
              </Td>
              {keepsMining && (
                <Td
                  color="var(--load-at-limit)"
                  title={`導入継続の最大は${peakDay(row.atLimitPeakDay)}前後`}
                >
                  {row.atLimitAverage.toFixed(0)} / {row.atLimitPeak.toLocaleString()}
                </Td>
              )}
              <Td tone="muted" title={`最大は${peakDay(row.behindPeakDay)}`}>
                {row.behindAverage.toFixed(0)} / {row.behindPeak.toLocaleString()}
              </Td>
              {/* Δ is PROJECTED against behind, matching the sentence above the table. The
                  scheduled column is still shown beside it, so the comparison the model is being
                  judged on stays on screen — but the delta that is worth acting on is this one. */}
              <Td
                color={
                  row.modelledDelta === null
                    ? "var(--ink-4)"
                    : row.modelledDelta > 0
                      ? "var(--accent-text)"
                      : "var(--ink-3)"
                }
              >
                {/* `null` renders as "—", never as 0% or ∞: on a fresh collection there is no
                    trailing window to compare against, and saying so is the honest answer. */}
                {row.modelledDelta === null
                  ? "—"
                  : `${row.modelledDelta > 0 ? "+" : ""}${(row.modelledDelta * 100).toFixed(0)}%`}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* **The caveat is gone from the screen** (Alex, 8 Aug 2026: copy is for the reader, not a
          note to the PM). It said what `future_due` cannot contain — lapses, unseen new cards, the
          next review of anything due before the window — which is true, is why the 365-day average
          reads 19 against a habit of hundreds a day, and is a paragraph of apology on a panel. The
          answer is a forecast worth printing rather than a disclaimer under a weak one: 日本-340. */}
    </Panel>
  );
}

/** A peak's date, short. Same construction as everywhere else on this tab — a bare date string is
 *  parsed as UTC and can land on the previous day. */
function peakDay(day: string): string {
  return dayMonthMaybeYear(new Date(`${day}T00:00:00`));
}

function BacklogPanel({ data }: { data: StatsHistory }) {
  const { backlog } = data;
  return (
    <Panel {...PANELS.backlog} gap={7}>
      <StatValue
        value={backlog.total.toLocaleString()}
        unit={UNITS.backlog}
        // **What the day asked of you, not what is left of it.** `fell due today` used to mean the
        // still-waiting part, so a day with 190 done and 4 left read `4 fell due today` — a real
        // number describing the remainder as if it were the day (Alex, 8 Aug 2026). Anki keeps no
        // record of a card's old due date, but the revlog does: previous review + `lastIvl`.
        note={`今日${backlog.becameDueToday.toLocaleString()}枚が期限。${backlog.doneToday.toLocaleString()}枚は完了。${backlog.carried.toLocaleString()}枚は繰り越し。`}
      />
    </Panel>
  );
}

/**
 * Why the projected curve might not be worth believing — and **nothing at all when it is**.
 *
 * Alex's standing rule for these screens is that copy is for the reader rather than a note to the
 * author (8 Aug 2026, when two true paragraphs were deleted for saying what a number could not do).
 * So this is not a standing caveat about what a simulation is: every branch below is a state in
 * which the call *succeeds* and quietly answers a different question, and each names something that
 * can be fixed. On a healthy collection it renders nothing.
 */
function ForecastNote({ data }: { data: StatsHistory }) {
  const { forecast } = data;
  const problems: string[] = [];
  // Ordered by how badly each one distorts the curve. FSRS off at the collection level is the
  // whole scheduler being different from the one modelled — and it is a *collection* config, so a
  // restored .apkg comes up with fitted presets and the scheduler switched off.
  if (!forecast.fsrs) {
    problems.push("このコレクションでは FSRS が無効なので、実際には使っていないスケジューラを前提に予測しています");
  } else if (!forecast.paramsPresent) {
    problems.push("このデッキのプリセットに FSRS パラメータがないので、予測は自分の値ではなく既定値を使っています");
  }
  if (!forecast.pressRatioMeasured) {
    problems.push("予測を解答回数に換算するには復習履歴が少なすぎるので、低めに出ています");
  }
  if (problems.length === 0) return null;
  return (
    <p className="text-meta text-ink-3">
      予測負荷: {problems.join("。")}。
    </p>
  );
}

function LimitsPanel({ data }: { data: StatsHistory }) {
  const peak = Math.max(...data.load.bars.filter((b) => b.future).map((b) => b.count), 0);
  const limit = data.limits.reviewsPerDay;
  return (
    <Panel {...PANELS.limits} gap={7}>
      <StatList
        rows={[
          ["最大日・上限前", `${peak.toLocaleString()}回`],
          [
            "最大日・上限後",
            `${(limit > 0 ? Math.min(peak, limit) : peak).toLocaleString()}回`,
          ],
          ["1日の復習上限", limit > 0 ? `${limit.toLocaleString()}回` : "設定なし"],
          // Frame `8a`'s fifth row, which shipped missing. Every term is measured: the cap is the
          // deck preset's, the load is the trailing 30 days, and the cost of a new card follows
          // real cards through a full 90-day window rather than trusting the design's ≈9.
          ["上限が効き始める", clippingLabel(data)],
          // The sibling row, and the one that genuinely needed the model: it asks WHEN a sustained
          // rate pushes load past the cap, which is a ramp rather than a steady state. The rate is
          // in the label because the date means nothing without it.
          [
            `最初に切られる日・${data.clippingRate.toLocaleString()}枚/日`,
            firstClippedLabel(data),
          ],
          ["新規上限", `${data.limits.newPerDay.toLocaleString()}枚/日`],
        ]}
      />
    </Panel>
  );
}

/**
 * The `Cap starts clipping` value, and the three states it distinguishes.
 *
 * **Zero is a real answer and must not read as a missing one**: a deck already at its cap has no
 * headroom, and that is the finding — adding anything there makes the backlog worse. `null` is the
 * genuinely unanswerable case and says which question it cannot answer.
 *
 * A fallback cost is marked `≈`, because the whole row divides by it. Presenting an assumption as
 * a measurement is the one thing on this panel that would change a decision, which is the rule
 * Pace's review-cost column already follows.
 */
function clippingLabel(data: StatsHistory): string {
  const headroom = data.capHeadroomNewCards;
  if (headroom === null) {
    return data.limits.reviewsPerDay > 0 ? "履歴不足" : "上限なし";
  }
  const rounded = Math.floor(headroom);
  const mark = data.reviewsMeasured ? "" : "≈";
  if (rounded <= 0) return `${mark}すでに上限`;
  return `${mark}新規${rounded.toLocaleString()}枚/日`;
}

/**
 * `First clipped day` — **a month, never a date.**
 *
 * `simulate` is a single Monte Carlo trajectory with no seed exposed: measured over 30 seeds, its
 * annual mean holds to about 1% while the *date* of its peak has a standard deviation of 37 days.
 * The server already reads the crossing off a smoothed curve; a month is as much precision as even
 * that can carry, and it is what the frame draws.
 */
function firstClippedLabel(data: StatsHistory): string {
  if (data.limits.reviewsPerDay <= 0) return "上限なし";
  if (data.clippingRate <= 0) return "新規なし";
  if (data.firstClippedDay === null) {
    // The English carried a plural ternary here; Japanese has no plural, so the whole branch
    // collapses to one string. Rule 4's usual case.
    const years = Math.round(data.clippingHorizonDays / 365);
    return `${years}年以内にはなし`;
  }
  const when = new Date(`${data.firstClippedDay}T00:00:00`);
  // Today's own month means it is clipping now, which is a state rather than a forecast.
  const now = new Date(`${data.today}T00:00:00`);
  if (when.getFullYear() === now.getFullYear() && when.getMonth() === now.getMonth()) {
    return "すでに上限超過";
  }
  return when.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

/**
 * Has the projection actually been right? — 日本-340's acceptance test on screen.
 *
 * **Both curves, always.** The question the ticket set is not how wrong the model is but whether it
 * beats `future_due`, the floor it replaced. Printing only the model's error would answer the easy
 * half and let a bad model look fine.
 *
 * **Three empty states, told apart** — the discipline Calibration already follows, because each
 * sends you somewhere different: nothing recorded yet (open this tab and come back tomorrow),
 * recorded but nothing has elapsed (wait), and elapsed but too few days to report a rate (wait
 * longer). A single "no data" would send you looking for a bug in two of the three.
 */
function AccuracyPanel({ deck }: { deck: string }) {
  const [data, setData] = useState<StatsBacktest | null>(null);

  useEffect(() => {
    let live = true;
    setData(null);
    // Failure is silent on purpose: this panel explains the forecast, and a red banner over the
    // whole tab because a diagnostic could not load would be worse than its absence.
    void statsBacktest(deck)
      .then((found) => live && setData(found))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [deck]);

  if (!data) return null;

  // Both plural ternaries collapse — Japanese counts the same at one and at many, so `1件` and
  // `38件` take one template between them. Rule 4's usual case, twice in one expression.
  const note =
    data.snapshots === 0
      ? "まだ予測が記録されていません。このタブをその日はじめて開いたときに1件保存されます。"
      : data.scoredDays === 0
        ? `予測を${data.snapshots}件記録しましたが、対象の日はまだどれも終わっていません。`
        : data.modelled.meanError === null
          ? `採点できたのは${data.scoredDays}日分で、誤差を出すには少なすぎます。`
          : null;

  return (
    <Panel {...PANELS.accuracy} gap={7}>
      {note ? (
        <p className="text-[15px]/[1.5] text-ink-2">{note}</p>
      ) : (
        <>
          <StatList
            rows={[
              ["採点日数", data.scoredDays.toLocaleString()],
              ["予測 — 平均誤差", `${data.modelled.meanError!.toFixed(0)}回/日`],
              ["予測 — 偏り", signed(data.modelled.bias)],
              ["予定 — 平均誤差", accuracy(data.scheduled.meanError)],
              ["予定 — 偏り", signed(data.scheduled.bias)],
            ]}
          />
          {data.modelWins !== null && (
            <p className="text-meta text-ink-3">
              {data.modelWins
                ? "予測のほうが Anki の予定より近い。"
                : "Anki の予定のほうが近い — 予測はまだ役に立っていません。"}
            </p>
          )}
        </>
      )}
    </Panel>
  );
}

/** `—` rather than 0 for an unmeasured figure: a rate with nothing behind it is not zero. */
function accuracy(value: number | null): string {
  return value === null ? "—" : `${value.toFixed(0)}回/日`;
}

/** Signed, and the sign is the content: **negative forecast too little.** */
function signed(value: number | null): string {
  if (value === null) return "—";
  return `${value > 0 ? "+" : ""}${value.toFixed(0)}回/日`;
}

function Key({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-[5px] text-[14px]/[1.4] text-ink-2">
      <span aria-hidden className="h-[11px] w-[11px] rounded-[var(--r-2)]" style={{ background: color }} />
      {label}
    </span>
  );
}


/*
 * History before its payload — each panel's own shape, drawn in that panel's place by `History`
 * while the request that fills it is in flight. One per panel rather than one for the tab, because
 * the two columns now arrive from two requests (日本-718).
 *
 * The heatmap's block is deliberately one band rather than a grid of pale cells: a fake grid at a
 * glance is indistinguishable from a real year with nothing in it, which is the one reading this
 * panel must never invite.
 */

function ReviewsSkeleton() {
  return (
    <Panel {...PANELS.reviewsPerDay} gap={10}>
      <SkeletonChart height={560} />
    </Panel>
  );
}

function StreakSkeleton() {
  return (
    <Panel {...PANELS.streak} gap={8}>
      <StatSkeleton note={UNITS.streak} width={110} />
    </Panel>
  );
}

function FiguresSkeleton() {
  return (
    <Panel {...PANELS.figures} gap={8}>
      <SkeletonRows rows={5} height={12} gap={14} />
    </Panel>
  );
}

function LoadSkeleton() {
  return (
    <Panel {...PANELS.load} title={loadTitle(LOAD_HALF_WINDOW)} gap={8}>
      <SkeletonChart height={120} />
    </Panel>
  );
}

function ComparisonSkeleton() {
  return (
    <Panel {...PANELS.comparison} gap={8}>
      <SkeletonRows rows={3} height={16} gap={18} />
    </Panel>
  );
}

function BacklogSkeleton() {
  return (
    <Panel {...PANELS.backlog} gap={8}>
      <StatSkeleton note={UNITS.backlog} width={84} />
    </Panel>
  );
}

function LimitsSkeleton() {
  return (
    <Panel {...PANELS.limits} gap={8}>
      <SkeletonRows rows={4} height={12} gap={14} />
    </Panel>
  );
}
